// BookingReview: Staff approve/reject, upload verification

import React, { useEffect, useState } from 'react';
import StaffLayout from '../../layouts/StaffLayout';
import { Box, Typography, Card, CardContent, Button, Grid, IconButton, Modal, Divider, Tooltip, Chip, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api from '../../services/api';
import AddIcon from '@mui/icons-material/Add';
import BookingModal from '../../components/BookingModal';
import { useProfile } from '../../context/ProfileContext';

function BookingReview() {
      // Payment request email dialog state
      const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
      const [paymentEmailLoading, setPaymentEmailLoading] = useState(false);
      const [paymentEmailResult, setPaymentEmailResult] = useState('');
      const [paymentEmail, setPaymentEmail] = useState('');
      const [paymentMsg, setPaymentMsg] = useState('');
      const [offlinePaymentDetails, setOfflinePaymentDetails] = useState('');
      // Document request dialog state
      const [docRequestOpen, setDocRequestOpen] = useState(false);
      const [docRequestMsg, setDocRequestMsg] = useState('');
      const [docRequestLoading, setDocRequestLoading] = useState(false);
      const [docList, setDocList] = useState('');
      const [docRequestResult, setDocRequestResult] = useState('');

      // Fetch offline payment details from settings on mount
      useEffect(() => {
        api.get('/api/settings').then(res => {
          setOfflinePaymentDetails(res.data.data?.offlinePaymentDetails || '');
        });
      }, []);

      // Send payment request email handler
      const handleSendPaymentRequest = async () => {
        if (!actionBooking) return;
        setPaymentEmailLoading(true);
        setPaymentEmailResult('');
        try {
          const email = actionBooking.userId?.email || actionBooking.bookingDetails?.email;
          const name = actionBooking.userId?.name || actionBooking.bookingDetails?.bookerName || 'User';
          const packageName = actionBooking.packageId?.name || actionBooking.bookingDetails?.packageName || '';
          const amount = actionBooking.packageId?.price || '';
          const msg = paymentMsg || offlinePaymentDetails || '';
          const subject = `Payment Request: ${packageName}`;
          const html = `<p>Dear ${name},</p><p>Please complete your payment for <b>${packageName}</b>.<br/>Amount: <b>${amount}</b></p><div style='margin:16px 0;padding:12px 16px;background:#e6f0ff;border-radius:8px;color:#007bff;font-weight:600;'>${msg}</div><p>Thank you!</p>`;
          await api.post('/api/utils/send-payment-request', { to: email, subject, html });
          setPaymentEmailResult('Payment request email sent successfully!');
        } catch (err) {
          setPaymentEmailResult(err.response?.data?.error || 'Failed to send payment request email');
        } finally {
          setPaymentEmailLoading(false);
        }
      };

      // Send document request handler
      const handleSendDocRequest = async () => {
        if (!actionBooking) return;
        setDocRequestLoading(true);
        setDocRequestResult('');
        try {
          // Compose a special message for document submission
          let msg = docRequestMsg || 'Please upload the required documents to complete your booking.';
          if (docList.trim()) {
            const docs = docList.split(',').map(d => d.trim()).filter(Boolean);
            if (docs.length > 0) {
              msg += '\n\nRequired Documents:\n' + docs.map((d, i) => `${i + 1}. ${d}`).join('\n');
            }
          }
          await api.post(`/api/bookings/${actionBooking._id}/request-docs`, { message: msg });
          setDocRequestResult('Document request email sent successfully!');
          setTimeout(() => {
            setDocRequestOpen(false);
            setDocRequestMsg('');
            setDocList('');
            setDocRequestResult('');
          }, 1200);
        } catch (err) {
          setDocRequestResult(err.response?.data?.error || 'Failed to send document request');
        } finally {
          setDocRequestLoading(false);
        }
      };
    // prefer server profile when available to avoid stale localStorage
    const { profile } = useProfile() || {};
    const user = profile || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {});

    // Helper to check if current user can perform staff actions on a booking
    const canAct = (booking) => {
      if (!booking) return false;
      if (user?.role === 'superadmin') return true;
      // Common fields that might indicate assignment
      const assigned = booking.assignedTo || booking.assignedStaffId || booking.staffId || booking.assigned;
      if (!assigned) return false;
      // assigned might be object or id
      const assignedId = typeof assigned === 'string' ? assigned : (assigned?._id || assigned?.id);
      return assignedId && user?._id && String(assignedId) === String(user._id);
    };

    // Action handlers
    const handleApprove = async (bookingId) => {
      try {
        await api.post(`/api/bookings/${bookingId}/approve`);
        setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: 'approved' } : b));
        if (selectedBooking && selectedBooking._id === bookingId) setSelectedBooking({ ...selectedBooking, status: 'approved' });
      } catch (e) {
        console.error('Approve failed', e);
        alert(e.response?.data?.error || 'Failed to approve booking');
      }
    };
    const handleReject = async (bookingId) => {
      try {
        await api.post(`/api/bookings/${bookingId}/reject`);
        setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: 'rejected', paymentStatus: 'rejected' } : b));
        if (selectedBooking && selectedBooking._id === bookingId) setSelectedBooking({ ...selectedBooking, status: 'rejected', paymentStatus: 'rejected' });
      } catch (e) {
        console.error('Reject failed', e);
        alert(e.response?.data?.error || 'Failed to reject booking');
      }
    };
    const handleDelete = async (bookingId) => {
      try {
        await api.delete(`/api/bookings/${bookingId}`);
        setBookings(prev => prev.filter(b => b._id !== bookingId));
        handleCloseModal();
      } catch (e) {
        console.error('Delete failed', e);
        alert(e.response?.data?.error || 'Failed to delete booking');
      }
    };
    const handleUploadVerification = async (bookingId) => {
      try {
        // Prompt for payment detail (bank/mobile money message)
        const paymentDetail = window.prompt('Enter payment detail (bank/mobile money message):');
        if (!paymentDetail) return;
        await api.patch(`/api/bookings/${bookingId}`, { paymentStatus: 'paid', paymentDetail });
        setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, paymentStatus: 'paid', paymentDetail } : b));
        if (selectedBooking && selectedBooking._id === bookingId) setSelectedBooking({ ...selectedBooking, paymentStatus: 'paid', paymentDetail });
      } catch (e) {
        console.error('Upload verification failed', e);
        alert(e.response?.data?.error || 'Failed to upload verification');
      }
    };
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionBooking, setActionBooking] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newBooking, setNewBooking] = useState({ name: '', email: '', packageId: '' });
  const [ticketDesigns, setTicketDesigns] = useState({});
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printPreviewData, setPrintPreviewData] = useState({ design: null, ticket: null });

  useEffect(() => {
    setLoading(true);
    api.get('/api/bookings')
      .then(res => setBookings(Array.isArray(res.data) ? res.data : (res.data.data || [])))
      .catch(e => { setBookings([]); })
      .finally(() => setLoading(false));
  }, []);

  const handleOpenModal = (booking) => {
    setSelectedBooking(booking);
    setModalOpen(true);
  };
  // small helper to show simple flag emojis for a few countries
  const countryToFlag = (country) => {
    if (!country) return '';
    const c = String(country).trim().toLowerCase();
    if (c.includes('tanzania') || c === 'tz' || c.includes('tan')) return '🇹🇿';
    if (c.includes('china') || c === 'cn') return '🇨🇳';
    if (c.includes('kenya') || c === 'ke') return '🇰🇪';
    if (c.includes('uganda') || c === 'ug') return '🇺🇬';
    return '';
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBooking(null);
  };

  const handleAddOpen = () => setAddOpen(true);
  const handleAddClose = () => { setAddOpen(false); setNewBooking({ name: '', email: '', packageId: '' }); };
  const handleBookingAdded = (created) => {
    if (created) setBookings(prev => [created, ...prev]);
  };

  return (
    <StaffLayout>
      <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Booking Review
          </Typography>
          { (user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superadmin') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOpen}>Add Booking</Button>
          ) : null }
        </Box>
        {loading ? <Typography>Loading...</Typography> : (
          <Grid container spacing={2}>
            {['pending', 'approved', 'rejected'].map(statusCat => (
              <Grid item xs={12} key={statusCat}>
                <Typography variant="h5" sx={{ mt: 3, mb: 1, color: statusCat === 'approved' ? 'green' : statusCat === 'rejected' ? 'red' : '#007bff', fontWeight: 700 }}>
                  {statusCat.charAt(0).toUpperCase() + statusCat.slice(1)} Bookings
                </Typography>
                <Grid container spacing={2}>
                  {bookings.filter(b => b.status === statusCat).map((booking) => (
                    <Grid item xs={12} md={6} lg={4} key={booking._id}>
                      <Card variant="outlined" sx={{ mb: 1, borderColor: statusCat === 'approved' ? 'green' : statusCat === 'rejected' ? 'red' : '#007bff', boxShadow: statusCat === 'pending' ? '0 0 4px #b2f5ea' : undefined, minHeight: 90, maxWidth: 320, p: 0.5 }}>
                        <CardContent sx={{ p: 1.2, '&:last-child': { pb: 1.2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap sx={{ fontSize: 15 }}>
                              {booking.bookingDetails?.bookerName || booking.bookingDetails?.companyDetails?.name || 'N/A'}
                            </Typography>
                                      {booking.flagged && <Chip label="Flagged" color="error" size="small" sx={{ ml: 1 }} />}
                                      {booking.ticketStatus && (
                                        <Tooltip title="Has Ticket">
                                          <ConfirmationNumberIcon color="success" sx={{ ml: 1 }} />
                                        </Tooltip>
                                      )}
                            {(() => {
                              let statusIcon = <HourglassEmptyOutlinedIcon style={{ color: '#f0ad4e', fontSize: 16, marginLeft: 6 }} titleAccess="Pending" />;
                              if (booking.status === 'approved' && booking.paymentStatus === 'paid') {
                                statusIcon = <CheckCircleIcon style={{ color: '#388e3c', fontSize: 16, marginLeft: 6 }} titleAccess="Approved & Paid" />;
                              } else if (booking.status === 'rejected' || booking.paymentStatus === 'rejected') {
                                statusIcon = <CancelIcon style={{ color: '#d32f2f', fontSize: 16, marginLeft: 6 }} titleAccess="Rejected" />;
                              }
                              if (booking.ticketStatus === 'created' || booking.ticketStatus === 'ticketed' || booking.ticketStatus === 'issued') {
                                statusIcon = <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 16, marginLeft: 6 }} titleAccess="Ticket Created" />;
                              }
                              return statusIcon;
                            })()}
                            {booking.paymentStatus === 'pending' ? (
                              <span title="Not Paid" style={{ color: '#d32f2f', fontSize: 16, marginLeft: 6 }}>$</span>
                            ) : booking.paymentStatus === 'paid' ? (
                              <span title="Paid" style={{ color: '#388e3c', fontSize: 16, marginLeft: 6 }}>$</span>
                            ) : (
                              <span style={{ fontWeight: 400, marginLeft: 6 }}>{booking.paymentStatus}</span>
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 13 }}>
                            <b>Email:</b> {booking.userId?.email || booking.bookingDetails?.email || 'N/A'}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                            <b>Package:</b> {booking.packageId?.name || booking.bookingDetails?.packageName || 'N/A'} | <b>Category:</b> {booking.packageId?.category || booking.bookingDetails?.category || 'N/A'}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                            <b>Company:</b> {booking.bookingDetails?.companyName || booking.bookingDetails?.companyDetails?.name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                            <b>Mobile:</b> {booking.bookingDetails?.mobile || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <b>Date:</b> {new Date(booking.createdAt).toLocaleString()}
                          </Typography>
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton onClick={() => handleOpenModal(booking)} aria-label="View">
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Actions">
                              <IconButton onClick={() => { setActionBooking(booking); setActionAnchorEl(booking._id); }} aria-label="Actions">
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Preview Ticket">
                              <IconButton size="small" color="primary" onClick={async () => {
                                const cat = booking.packageId?.category || booking.bookingDetails?.category || 'attendee';
                                let designs = ticketDesigns;
                                if (!designs || Object.keys(designs).length === 0) {
                                  try {
                                    const r = await api.get('/api/ticket-designs');
                                    designs = r.data || {};
                                    setTicketDesigns(designs);
                                  } catch (e) {
                                    designs = {};
                                  }
                                }
                                  const design = (function pickDesign(designsObj, c) { if (!designsObj || typeof designsObj !== 'object') return {}; if (c && designsObj[c]) return designsObj[c]; const keys = Object.keys(designsObj||{}); return keys.length ? designsObj[keys[0]] : {}; })(designs, cat);
                                const ticketForPreview = {
                                  type: cat,
                                  attendee: booking.bookingDetails?.bookerName || booking.bookingDetails?.companyDetails?.name || '',
                                  reference: booking.ticketReference || booking._id,
                                  status: booking.status,
                                  company: booking.bookingDetails?.companyDetails?.name || '',
                                  email: booking.userId?.email || booking.bookingDetails?.email || '',
                                  phone: booking.bookingDetails?.mobile || booking.bookingDetails?.phone || '',
                                  packageName: booking.packageId?.name || booking.bookingDetails?.packageName || '',
                                  peopleCount: booking.bookingDetails?.peopleCount
                                };
                                setPrintPreviewData({ design, ticket: ticketForPreview });
                                setPrintPreviewOpen(true);
                              }}>
                                <ConfirmationNumberIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Action Modal Popup */}
                            {actionAnchorEl === booking._id && actionBooking && (
                              <Modal open={true} onClose={() => { setActionAnchorEl(null); setActionBooking(null); }}>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 320, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#007bff', mb: 2 }}>Booking Actions</Typography>
                                  <Divider sx={{ mb: 2, width: '100%' }} />
                                  <Box sx={{ display: 'flex', gap: 3, mb: 2, justifyContent: 'center' }}>
                                    <Tooltip title="Approve">
                                      <span>
                                        <IconButton color="success" onClick={() => { handleApprove(actionBooking._id); setActionAnchorEl(null); setActionBooking(null); }} disabled={actionBooking.status === 'approved'} size="large">
                                          <CheckCircleIcon fontSize="inherit" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Reject">
                                      <IconButton color="error" onClick={() => { handleReject(actionBooking._id); setActionAnchorEl(null); setActionBooking(null); }} size="large">
                                        <CancelIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton color="error" onClick={() => { handleDelete(actionBooking._id); setActionAnchorEl(null); setActionBooking(null); }} size="large">
                                        <DeleteIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Send Payment Email">
                                      <IconButton color="info" onClick={() => {
                                        setPaymentEmail(actionBooking?.userId?.email || actionBooking?.bookingDetails?.email || '');
                                        setPaymentMsg(offlinePaymentDetails);
                                        setPaymentDialogOpen(true);
                                      }} size="large">
                                        <EmailIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Request Document">
                                      <IconButton color="primary" onClick={() => {
                                        setDocRequestOpen(true);
                                        setDocRequestMsg('Please upload the required documents to complete your booking.');
                                      }} size="large">
                                        <CloudUploadIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                          {/* Payment Request Email Dialog */}
                                          <Modal open={!!paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 340, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                              <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Send Payment Request Email</Typography>
                                              <Divider sx={{ mb: 2, width: '100%' }} />
                                              <TextField label="Recipient Email" fullWidth sx={{ mb: 2 }} value={paymentEmail} onChange={e => setPaymentEmail(e.target.value)} />
                                              <TextField label="Payment Instructions" fullWidth multiline minRows={3} sx={{ mb: 2 }} value={paymentMsg} onChange={e => setPaymentMsg(e.target.value)} />
                                              <Button variant="contained" color="primary" onClick={handleSendPaymentRequest} disabled={paymentEmailLoading || !paymentEmail} sx={{ width: '100%', fontWeight: 700, mb: 1 }}>{paymentEmailLoading ? 'Sending...' : 'Send Email'}</Button>
                                              <Button onClick={() => setPaymentDialogOpen(false)} sx={{ width: '100%' }}>Cancel</Button>
                                              {paymentEmailResult && <Typography color={paymentEmailResult.includes('success') ? 'success.main' : 'error.main'} sx={{ mt: 2 }}>{paymentEmailResult}</Typography>}
                                            </Box>
                                          </Modal>
                                          {/* Document Request Dialog */}
                                          <Modal open={!!docRequestOpen} onClose={() => setDocRequestOpen(false)}>
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 340, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                              <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Request Documents</Typography>
                                              <Divider sx={{ mb: 2, width: '100%' }} />
                                              <TextField label="Message" fullWidth multiline minRows={3} sx={{ mb: 2 }} value={docRequestMsg} onChange={e => setDocRequestMsg(e.target.value)} />
                                              <TextField label="Document List (comma separated)" fullWidth sx={{ mb: 2 }} value={docList} onChange={e => setDocList(e.target.value)} placeholder="e.g. ID, Passport, Payment Receipt" />
                                              <Button variant="contained" color="primary" onClick={handleSendDocRequest} disabled={docRequestLoading || (!docRequestMsg && !docList)} sx={{ width: '100%', fontWeight: 700, mb: 1 }}>{docRequestLoading ? 'Sending...' : 'Send Request'}</Button>
                                              <Button onClick={() => setDocRequestOpen(false)} sx={{ width: '100%' }}>Cancel</Button>
                                              {docRequestResult && <Typography color={docRequestResult.includes('success') ? 'success.main' : 'error.main'} sx={{ mt: 2 }}>{docRequestResult}</Typography>}
                                            </Box>
                                          </Modal>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
                                    Select an action for this booking. Actions are permanent.
                                  </Typography>
                                  <Button onClick={() => { setActionAnchorEl(null); setActionBooking(null); }} sx={{ mt: 2 }}>Close</Button>
                                </Box>
                              </Modal>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  {bookings.filter(b => b.status === statusCat).length === 0 && (
                    <Grid item xs={12}><Typography color="text.secondary">No {statusCat} bookings.</Typography></Grid>
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
        <Modal open={modalOpen} onClose={handleCloseModal}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 0, borderRadius: 3, minWidth: 340, maxWidth: 600 }}>
            {selectedBooking && (
              <Box sx={{ p: 3, maxHeight: '80vh', overflowY: 'auto' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#007bff' }}>
                  Booking Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {/* Show only booking form input fields with clear labels and icons */}
                {selectedBooking.bookingDetails ? (
                  <Box sx={{ pl: 1 }}>
                    {Object.entries(selectedBooking.bookingDetails).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No details provided.</Typography>
                    )}
                    {Object.entries(selectedBooking.bookingDetails).map(([key, value]) => {
                      // Icon mapping for known fields
                      let icon = null;
                      if (key.toLowerCase().includes('hotel') || key.toLowerCase().includes('accommodation')) icon = <span role="img" aria-label="hotel">🏨</span>;
                      if (key.toLowerCase().includes('airport')) icon = <span role="img" aria-label="airport">✈️</span>;
                      // Format value
                      let displayValue = value;
                      if (typeof value === 'object' && value !== null) {
                        displayValue = JSON.stringify(value);
                      }
                      return (
                        <Typography key={key} variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          {icon} <b style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}:</b> {displayValue}
                        </Typography>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No details provided.</Typography>
                )}
                <Divider sx={{ mb: 2 }} />
                {/* Uploaded Documents Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Uploaded Documents</Typography>
                  {selectedBooking.uploadedDocuments && selectedBooking.uploadedDocuments.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedBooking.uploadedDocuments.map((doc, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>{doc.name || `Document ${idx + 1}`}</Typography>
                          <Tooltip title="Preview Document"><IconButton color="info" onClick={() => window.open(doc.url, '_blank')}><VisibilityIcon /></IconButton></Tooltip>
                          <Tooltip title="Download Document"><IconButton color="primary" href={doc.url} download><CloudUploadIcon /></IconButton></Tooltip>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <PrintPreviewModal open={printPreviewOpen} onClose={() => setPrintPreviewOpen(false)} design={printPreviewData.design} ticket={printPreviewData.ticket} />
                {/* Review Actions Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Review Actions</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {canAct(selectedBooking) && selectedBooking.status !== 'approved' && (
                        <Tooltip title="Approve">
                          <IconButton color="success" onClick={() => handleApprove(selectedBooking._id)}><CheckCircleIcon /></IconButton>
                        </Tooltip>
                      )}
                      {canAct(selectedBooking) && (
                        <Tooltip title="Reject">
                          <IconButton color="error" onClick={() => handleReject(selectedBooking._id)}><CancelIcon /></IconButton>
                        </Tooltip>
                      )}
                      {canAct(selectedBooking) && (
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => handleDelete(selectedBooking._id)}><DeleteIcon /></IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Send Payment Email">
                        <IconButton color="info" /* TODO: implement send payment email */><EmailIcon /></IconButton>
                      </Tooltip>
                      {canAct(selectedBooking) && (
                        <Tooltip title="Upload Verification">
                          <IconButton color="primary" onClick={() => handleUploadVerification(selectedBooking._id)}><CloudUploadIcon /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Modal>
        {/* Removed legacy Menu actions, now handled inline on cards */}
        {/* Add Booking via BookingModal */}
        <BookingModal open={addOpen} onClose={handleAddClose} onBookingAdded={handleBookingAdded} manual />
      </Box>
    </StaffLayout>
  );
}

export default BookingReview;
